import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {minimizeCommentMutation as minimizeCommentMutationQueryId} from '@github-ui/server-persisted-queries'
import type {
  MinimizeCommentMutationMutation,
  ReportedContentClassifiers,
} from '@github-ui/server-persisted-queries/types/minimizeCommentMutation'
import {type JSONRequestInit, reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import type {Disposable, Environment, RecordSourceProxy, RecordSourceSelectorProxy} from 'relay-runtime'

function minimizationPath(owner?: string, repo?: string): string {
  if (owner && repo) {
    return `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/comments/minimization?queryId=${minimizeCommentMutationQueryId}`
  }

  const [, inferredOwner, inferredRepo] = window.location.pathname.split('/')
  if (!inferredOwner || !inferredRepo) {
    throw new Error('Failed to infer repository path for minimizing comment')
  }

  return `/${encodeURIComponent(inferredOwner)}/${encodeURIComponent(inferredRepo)}/comments/minimization?queryId=${minimizeCommentMutationQueryId}`
}

function writeRelayMinimizationState(
  store: RecordSourceProxy | RecordSourceSelectorProxy,
  id: string,
  fields: {
    isHidden: boolean
    minimizedReason: string | null
    pendingMinimizeReason: string | null
    isMinimized: boolean
  },
) {
  const comment = store.get(id)
  comment?.setValue(fields.isHidden, 'isHidden')
  comment?.setValue(fields.minimizedReason, 'minimizedReason')
  comment?.setValue(fields.pendingMinimizeReason, 'pendingMinimizeReason')
  comment?.setValue(fields.isMinimized, 'isMinimized')
}

type MinimizeOperation = SetFieldsOperation & {
  minimizeInput: {
    reason: ReportedContentClassifiers
    owner?: string
    repo?: string
    onCompleted?: () => void
    onError?: (error?: Error) => void
  }
}

type MinimizeResult = {
  isMinimized: boolean
  minimizedReason: string | null
}

async function submitMinimizeComment(operation: SetFieldsOperation): Promise<MinimizeResult> {
  const {id, minimizeInput} = operation as MinimizeOperation
  const {reason, owner, repo} = minimizeInput
  const request: JSONRequestInit = {
    method: 'PUT',
    body: {input: {subjectId: id, classifier: reason}},
  }
  const response = await reactFetchJSON(minimizationPath(owner, repo), request)
  if (!response.ok) throw new Error(`Failed to minimize comment (${response.status})`)

  const payload = (await response.json()) as {
    errors?: Array<{message?: string}>
    data?: MinimizeCommentMutationMutation
  }
  const comment = payload.data?.minimizeComment?.minimizedComment
  if (!comment) throw new Error(payload.errors?.[0]?.message ?? 'Failed to minimize comment')
  return {isMinimized: comment.isMinimized, minimizedReason: comment.minimizedReason ?? null}
}

export function useMinimizeCommentMutation({environment}: {environment: Environment}) {
  const mutation = useEntityMutation<MinimizeResult, Error, Disposable | undefined>({
    mutationFn: submitMinimizeComment,
    getConfirmedOperation: (result, operation) => ({
      type: 'setFields',
      id: operation.id,
      fields: {
        isHidden: result.isMinimized,
        minimizedReason: result.minimizedReason,
        pendingMinimizeReason: result.minimizedReason,
      },
    }),
    onMutate: ({operation}) => {
      const {id, fields} = operation
      return typeof environment.applyUpdate === 'function'
        ? environment.applyUpdate({
            storeUpdater: store =>
              writeRelayMinimizationState(store, id, {
                isHidden: Boolean(fields.isHidden),
                minimizedReason: fields.minimizedReason as string | null,
                pendingMinimizeReason: fields.pendingMinimizeReason as string | null,
                isMinimized: true,
              }),
          })
        : undefined
    },
    onSuccess: (result, {operation}, relayUpdate) => {
      const {minimizeInput} = operation as MinimizeOperation
      environment.commitUpdate(store => {
        writeRelayMinimizationState(store, operation.id, {
          isHidden: result.isMinimized,
          minimizedReason: result.minimizedReason,
          pendingMinimizeReason: result.minimizedReason,
          isMinimized: result.isMinimized,
        })
      })
      relayUpdate?.dispose()
      minimizeInput.onCompleted?.()
    },
    onError: (error, {operation}, relayUpdate) => {
      relayUpdate?.dispose()
      ;(operation as MinimizeOperation).minimizeInput.onError?.(error)
    },
  })

  return useCallback(
    ({
      input: {id, reason, owner, repo},
      onCompleted,
      onError,
    }: {
      input: {id: string; reason: ReportedContentClassifiers; owner?: string; repo?: string}
      onCompleted?: () => void
      onError?: (error?: Error) => void
    }) => {
      const operation: MinimizeOperation = {
        type: 'setFields',
        id,
        fields: {isHidden: true, minimizedReason: reason, pendingMinimizeReason: reason},
        minimizeInput: {reason, owner, repo, onCompleted, onError},
      }
      try {
        mutation.mutate({operation})
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Failed to minimize comment'))
      }
    },
    [mutation],
  )
}
